const MODELS = [
  "gemini-3.5-flash",
  "gemini-3.6-flash",
  "gemini-2.5-flash",
  "gemini-flash-latest",
];

export const getGeminiKey = () => process.env.REACT_APP_GEMINI_API_KEY || "";

const flattenQuestions = (node, acc = []) => {
  if (Array.isArray(node)) node.forEach((q) => acc.push(q));
  else if (node && typeof node === "object") Object.values(node).forEach((v) => flattenQuestions(v, acc));
  return acc;
};

const sourceNotes = (essay, questionFiles) => {
  const wanted = new Set(essay.sourceQuestionEns || []);
  if (!wanted.size) return "";
  const notes = [];
  flattenQuestions(questionFiles).forEach((q) => {
    if (!wanted.has(q?.question?.en)) return;
    const correct = (q.answers || []).find((a) => a.correct);
    notes.push(
      `- Q: ${q.question?.en || ""}\n  Answer: ${correct?.text?.en || ""}\n  Why: ${correct?.explanation?.en || ""}`
    );
  });
  return notes.slice(0, 10).join("\n");
};

const parseJson = (text) => {
  if (!text) return null;
  const cleaned = text.replace(/```json|```/g, "").trim();
  const start = cleaned.indexOf("{");
  const end = cleaned.lastIndexOf("}");
  if (start < 0 || end < start) return null;
  try {
    return JSON.parse(cleaned.slice(start, end + 1));
  } catch {
    return null;
  }
};

const callGemini = async (key, model, prompt) => {
  const res = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${encodeURIComponent(key)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          temperature: 0.4,
          responseMimeType: "application/json",
        },
      }),
    }
  );
  const data = await res.json();
  if (!res.ok) {
    const message = data?.error?.message || `Gemini ${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }
  return data?.candidates?.[0]?.content?.parts?.map((p) => p.text).join("\n") || "";
};

const shortenLine = (text) => {
  const t = (text || "").replace(/\s+/g, " ").trim();
  if (!t) return "";
  const first = t.split(/(?<=[.!?。])\s+/)[0];
  if (first.length <= 110) return first.replace(/[.。]$/, "");
  return `${first.slice(0, 107).replace(/\s+\S*$/, "")}…`;
};

export const polishEssayWithGemini = async (draft, questionFiles) => {
  const key = getGeminiKey();
  if (!key || !draft) return draft;

  const facts = sourceNotes(draft, questionFiles);
  const prompt = `You are a wine-education tutor writing a short-answer practice question.

Rewrite this into ONE short-answer question and a model answer.

Draft question (EN): ${draft.question?.en || ""}
Draft question (CN): ${draft.question?.cn || ""}
Topic: ${draft.topic?.en || ""}

Source facts from the student's quiz bank:
${facts || "(no extra facts — use accurate wine knowledge for this topic)"}

Rules:
- Question style: Explain / Compare / Discuss / Identify and explain.
- Model answer: 5 to 8 bullet points.
- EACH bullet must be ONE short sentence OR key words only (max 15 words).
- Do NOT write paragraphs. Do NOT join several ideas with semicolons into a long line.
- Good: "Syrah: high tannin, deep colour, black pepper"
- Good: "Winemaking: destem, warm ferment, French oak"
- Bad: a long paragraph covering climate, grapes and oak in one bullet.
- When grapes appear, give characteristics as key words (acid, tannin, colour, flavour).
- When style appears, give winemaking as key words (oak, MLF, stainless, blend, maceration).
- Do not mention WSET, D3, Diploma, examiner, or similar branded exam names in the question or answer.
- Every field must have English and Chinese.
- Return JSON only with this shape:
{
  "question": { "en": "", "cn": "" },
  "topic": { "en": "", "cn": "" },
  "bullets": [{ "en": "", "cn": "" }]
}`;

  let lastError = null;
  for (const model of MODELS) {
    try {
      const text = await callGemini(key, model, prompt);
      const parsed = parseJson(text);
      if (!parsed?.question?.en || !Array.isArray(parsed.bullets) || parsed.bullets.length === 0) {
        continue;
      }
      return {
        ...draft,
        question: {
          en: parsed.question.en,
          cn: parsed.question.cn || draft.question?.cn || parsed.question.en,
        },
        topic: {
          en: parsed.topic?.en || draft.topic?.en,
          cn: parsed.topic?.cn || draft.topic?.cn,
        },
        bullets: parsed.bullets
          .filter((b) => b?.en || b?.cn)
          .map((b) => ({
            en: shortenLine(b.en || ""),
            cn: shortenLine(b.cn || b.en || ""),
          })),
        ai: "gemini",
      };
    } catch (err) {
      lastError = err;
      const msg = (err.message || "").toLowerCase();
      const missingModel = err.status === 404 || msg.includes("not found") || msg.includes("not supported");
      if ((err.status === 400 || err.status === 403) && !missingModel) throw err;
    }
  }
  if (lastError) throw lastError;
  return draft;
};
